import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { FixedByDepartureCostPriceItem, SlidingScaleByDeparturePricingItem } from '~/types/tours/costPrice';
import { CostPriceKind } from '~/types/tours/supplier';

import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import { DepartureItem, DepartureRow, hasPrice } from './departureRows';

interface Props {
  rows: Array<DepartureRow<DepartureItem>>;
  /** How the service is costed, which decides what a departure's prices look like. */
  kind: CostPriceKind;
  /** Opens the upload for a departure, or blank to price several at once. */
  onUpload: (departure: string | null) => void;
}

function formatDate(date?: string): string {
  return date ? dayjs(date).format('D MMM YYYY') : 'Not on this tour';
}

/**
 * One departure's cost prices at a time.
 *
 * A service can run dozens of departures, and only one is being looked at, so
 * the departure is chosen and its scale shown in full rather than every
 * departure being listed and expanded one at a time.
 */
export default function DepartureCostPrices({ rows, kind, onUpload }: Props) {
  const [selectedKey, setSelectedKey] = useState<string>(rows[0]?.key ?? '');

  const selected = useMemo(() => rows.find((row) => row.key === selectedKey) ?? rows[0], [rows, selectedKey]);

  const unknown = rows.filter((row) => !row.isKnownDeparture);

  if (!rows.length) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" startIcon={<AddIcon />} onClick={() => onUpload(null)}>
            Add prices
          </Button>
        }
      >
        This tour has no departures to price yet.
      </Alert>
    );
  }

  const priced = !!selected && hasPrice(selected);

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          select
          label="Departure"
          value={selected?.key ?? ''}
          onChange={(event) => setSelectedKey(event.target.value)}
          sx={{ minWidth: 420 }}
          SelectProps={{
            MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
            renderValue: (value) => {
              const row = rows.find((candidate) => candidate.key === value);

              return row ? `${row.departureName} · ${formatDate(row.departureDate)}` : '';
            },
          }}
        >
          {rows.map((row) => (
            <MenuItem key={row.key} value={row.key}>
              <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ minWidth: 116 }}>
                  {row.departureName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(row.departureDate)}
                </Typography>
                <Box flex={1} />
                {!hasPrice(row) && (
                  <Typography variant="caption" color="warning.main">
                    No price
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" onClick={() => onUpload(selected?.departure ?? null)}>
          {priced ? 'Edit prices' : 'Add prices'}
        </Button>
      </Stack>

      {!!unknown.length && (
        <Alert severity="error">
          {unknown.length} price{unknown.length > 1 ? 's are' : ' is'} stored against a departure this tour does not
          run, so nothing will resolve against {unknown.length > 1 ? 'them' : 'it'}:{' '}
          {unknown.map((row) => row.departureName).join(', ')}.
        </Alert>
      )}

      {!!selected && !priced && (
        <Alert severity="info">
          {selected.departureName} has no cost price yet
          {selected.departureDate ? `, and leaves ${formatDate(selected.departureDate)}` : ''}.
        </Alert>
      )}

      {!!selected &&
        priced &&
        (kind === 'FixedByDeparture' ? (
          <FixedPriceTable prices={selected.items as Array<FixedByDepartureCostPriceItem>} />
        ) : (
          <SlidingScaleTable pricing={selected.items as Array<SlidingScaleByDeparturePricingItem>} />
        ))}
    </Stack>
  );
}
